import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemButton, 
  ListItemIcon, ListItemText, Box, IconButton, Avatar, Menu, MenuItem, 
  Divider, Badge, useTheme, Tooltip, Collapse
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutline';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import BarChartIcon from '@mui/icons-material/BarChartOutlined';
import MailIcon from '@mui/icons-material/MailOutline';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import GavelIcon from '@mui/icons-material/GavelOutlined';
import PinDropIcon from '@mui/icons-material/PinDropOutlined';
import FolderIcon from '@mui/icons-material/FolderOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined';
import GroupAddIcon from '@mui/icons-material/GroupAddOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ComputerIcon from '@mui/icons-material/Computer';
import StoreIcon from '@mui/icons-material/Store';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../assets/acts_logo.png';

const drawerWidth = 250;

function Layout({ logout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleGroup = (title) => {
    setOpenGroups(prev => ({
      ...prev,
      [title]: prev[title] === false ? true : false
    }));
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const fullName = localStorage.getItem('full_name') || 'User';
  const role = localStorage.getItem('user_role') || 'Member';
  const divisions = JSON.parse(localStorage.getItem('user_divisions') || '[]');
  const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

  const menuGroups = [
      {
          title: 'MAIN',
          items: [
              { text: 'Dashboard', icon: <DashboardIcon />, path: '/', subtitle: 'Overview and metrics' },
              { text: 'Projects', icon: <FolderIcon />, path: '/projects', subtitle: 'Manage projects' },
              { text: 'Contracts', icon: <GavelIcon />, path: '/contracts', subtitle: 'Manage contracts' },
              { text: 'Hierarchy', icon: <AccountTreeIcon />, path: '/team', subtitle: 'IT hierarchy' },
              { text: 'KPIs', icon: <BarChartIcon />, path: '/kpis', subtitle: 'Performance metrics' },
              { text: 'Clients', icon: <BusinessIcon />, path: '/clients', subtitle: 'Manage clients' },
              { text: 'References', icon: <MailIcon />, path: '/references', subtitle: 'Support emails' },
              { text: 'TimeSheet', icon: <AssignmentIcon />, path: '/datasheets', subtitle: 'Time sheet' },
              { text: 'Punching', icon: <PinDropIcon />, path: '/punching', subtitle: 'Attendance logs' },
          ]
      },
      ...((role === 'Admin' || divisions.includes('IT') || divisions.includes('Software')) ? [{
          title: 'SUPPORT',
          items: [
              { text: 'IT Dashboard', icon: <ComputerIcon />, path: '/support-dashboard', subtitle: 'IT Overview' },
              { text: 'IT Projects', icon: <AssignmentIcon />, path: '/it-projects', subtitle: 'Manage IT projects' },
              { text: 'IT Suppliers', icon: <StoreIcon />, path: '/it-suppliers', subtitle: 'Manage IT suppliers' },
              { text: 'Software Licenses', icon: <WorkspacePremiumIcon />, path: '/software-licenses', subtitle: 'Manage licenses & renewals' },
              { text: 'Tickets', icon: <ConfirmationNumberIcon />, path: '/tickets', subtitle: 'Manage support tickets' },
              { text: 'Inbox', icon: <MailIcon />, path: '/inbox', subtitle: 'Company Inbox' },
          ]
      }] : []),
      ...(role === 'Admin' ? [{
          title: 'SETUP',
          items: [
              { text: 'Setup', icon: <SettingsIcon />, path: '/setup', subtitle: 'System configuration' }
          ]
      }] : [])
  ];

  const drawerContent = (
      <>
        {/* Brand / Logo Area */}
        <Box sx={{ py: 2, pl: 3, pr: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <img src={logo} alt="ACTS" style={{ maxWidth: '60%', height: 'auto' }} />
        </Box>

        {/* Navigation Items */}
        <Box sx={{ overflow: 'auto', px: 2, flexGrow: 1 }}>
            <List sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}>
                {menuGroups.map((group, gIndex) => {
                    const isOpen = openGroups[group.title] !== false;
                    return (
                        <Box key={group.title} sx={{ mb: 1 }}>
                            <Box 
                                onClick={() => handleToggleGroup(group.title)}
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between', 
                                    px: 2, 
                                    py: 1, 
                                    cursor: 'pointer',
                                    mt: gIndex > 0 ? 2 : 0,
                                    borderRadius: 1,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#94A3B8', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                    {group.title}
                                </Typography>
                                {isOpen ? <ExpandLess sx={{ color: '#94A3B8', fontSize: '1rem' }} /> : <ExpandMore sx={{ color: '#94A3B8', fontSize: '1rem' }} />}
                            </Box>
                            
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {group.items.map((item) => {
                                        const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
                                        return (
                                            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                                                <ListItemButton 
                                                    onClick={() => {
                                                        navigate(item.path);
                                                        setMobileOpen(false);
                                                    }}
                                                    sx={{
                                                        borderRadius: 2,
                                                        bgcolor: isActive ? '#EFF6FF' : 'transparent', 
                                                        color: isActive ? '#2563EB' : '#64748B',
                                                        '&:hover': {
                                                            bgcolor: isActive ? '#EFF6FF' : '#F8FAFC',
                                                            color: isActive ? '#2563EB' : '#334155',
                                                        },
                                                        py: 1,
                                                        px: 2
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                                        {item.icon}
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={item.text} 
                                                        secondary={item.subtitle}
                                                        primaryTypographyProps={{ 
                                                            fontWeight: isActive ? '700' : '500',
                                                            fontSize: '0.9rem',
                                                            color: isActive ? '#1E293B' : 'inherit'
                                                        }} 
                                                        secondaryTypographyProps={{
                                                            fontSize: '0.7rem',
                                                            color: isActive ? '#60A5FA' : '#94A3B8',
                                                            lineHeight: 1.2
                                                        }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </Box>
                    );
                })}
            </List>
        </Box>
        
        {/* Sidebar Footer - User Profile */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <ListItemButton 
                onClick={handleMenu}
                sx={{ 
                    borderRadius: 2, 
                    '&:hover': { bgcolor: '#F8FAFC' } 
                }} 
            >
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 'bold', fontSize: '0.85rem', mr: 2 }}>{initials}</Avatar>
                <ListItemText 
                    primary={fullName} 
                    secondary={role}
                    primaryTypographyProps={{ fontWeight: 700, fontSize: '0.85rem', color: '#1E293B' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748B' }}
                />
            </ListItemButton>
            <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        borderRadius: 3,
                        minWidth: 180,
                        ml: 2
                    }
                }}
            >
                <MenuItem onClick={handleLogout} sx={{ gap: 1.5, color: '#DC2626', fontWeight: 600 }}>
                    <LogoutIcon fontSize="small" /> Logout
                </MenuItem>
            </Menu>
        </Box>
      </>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#F1F5F9', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          display: { sm: 'none' },
          bgcolor: '#FFFFFF',
          color: '#334155',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold', color: '#1E293B', fontSize: '1rem' }}>
             ACTS Project Manager
          </Typography>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
            keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                bgcolor: '#FFFFFF', 
                color: '#334155',
                borderRight: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
            },
            }}
        >
            {drawerContent}
        </Drawer>
        <Drawer
            variant="permanent"
            sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                bgcolor: '#FFFFFF', 
                color: '#334155',
                borderRight: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '4px 0 24px -12px rgba(0,0,0,0.1)'
            },
            }}
            open
        >
            {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box component="main" sx={{ flexGrow: 1, p: 4, width: { sm: `calc(100% - ${drawerWidth}px)` }, overflowX: 'hidden' }}>
        <Toolbar sx={{ display: { sm: 'none' } }} />
        <Outlet />
      </Box>
    </Box>
  );
}

export default Layout;
